'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { keyframes } from '@mui/system';

import { useT } from '@/hooks';
import { Confetti } from './Confetti';
import { BRAND } from './onboarding.constants';

const START_PRICE = 56; // cents (Yes)
const REQUIRED_TAPS = 5; // taps to fill the order
const POP_EMOJIS = ['🚀', '🔥', '⚡', '💰', '📈'];

const floatUp = keyframes({
  '0%': { transform: 'translate(-50%, 0) scale(0.7)', opacity: 0 },
  '15%': { opacity: 1 },
  '100%': { transform: 'translate(-50%, -76px) scale(1.15)', opacity: 0 },
});
const bump = keyframes({
  '0%': { transform: 'scale(1)' },
  '35%': { transform: 'scale(1.22)' },
  '100%': { transform: 'scale(1)' },
});
const shake = keyframes({
  '0%,100%': { transform: 'translateX(0)' },
  '20%': { transform: 'translateX(-7px)' },
  '40%': { transform: 'translateX(7px)' },
  '60%': { transform: 'translateX(-5px)' },
  '80%': { transform: 'translateX(5px)' },
});

/**
 * Final onboarding step: a Porvenix-style market ticket. The user spams "Buy Yes"
 * to place their order — each tap nudges the odds (live chart), floats an emoji, and
 * fills the progress. The final tap shakes the ticket, drops confetti, then
 * `onComplete()` reveals the trending/home page.
 */
export function TradeDemo({ onComplete }: { onComplete: () => void }) {
  const { t } = useT();
  const [taps, setTaps] = useState(0);
  const [price, setPrice] = useState(START_PRICE); // Yes price, cents
  const [history, setHistory] = useState<number[]>([52, 53, 51, 54, 55, START_PRICE]);
  const [pops, setPops] = useState<{ id: number; emoji: string }[]>([]);
  const [done, setDone] = useState(false);
  const idRef = useRef(0);
  const timers = useRef<number[]>([]);

  const started = taps > 0;
  const progress = Math.min(100, (taps / REQUIRED_TAPS) * 100);

  // Ambient "live market" flicker before the user starts.
  useEffect(() => {
    if (started || done) return;
    const id = window.setInterval(() => {
      setPrice((p) => {
        const np = Math.min(60, Math.max(52, p + (Math.random() < 0.5 ? -1 : 1)));
        setHistory((h) => [...h.slice(-11), np]);
        return np;
      });
    }, 1600);
    return () => window.clearInterval(id);
  }, [started, done]);

  useEffect(() => () => timers.current.forEach((id) => window.clearTimeout(id)), []);

  const handleBuy = () => {
    if (done) return;
    const next = taps + 1;
    const slipped = Math.min(72, price + 1 + Math.floor(Math.random() * 3));
    setTaps(next);
    setPrice(slipped);
    setHistory((h) => [...h.slice(-11), slipped]);

    const id = idRef.current++;
    const emoji = POP_EMOJIS[next % POP_EMOJIS.length];
    setPops((p) => [...p, { id, emoji }]);
    const rm = window.setTimeout(() => setPops((p) => p.filter((x) => x.id !== id)), 850);
    timers.current.push(rm);

    if (next >= REQUIRED_TAPS) {
      setDone(true);
      timers.current.push(window.setTimeout(onComplete, 2400));
    }
  };

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Confetti fire={done} />

      <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.3rem', sm: '1.55rem' }, mb: 0.5 }}>
        {t('onboarding.demo.title', { defaultValue: 'Place your first trade' })}
      </Typography>
      <Typography sx={{ color: BRAND.textSecondary, fontSize: '0.88rem', mb: 2.5 }}>
        {t('onboarding.demo.subtitle', { defaultValue: 'Spam Buy to place your order' })}
      </Typography>

      {/* Market ticket */}
      <Box
        sx={{
          bgcolor: BRAND.cardRow,
          border: `1px solid ${done ? BRAND.green : BRAND.cardRowBorder}`,
          borderRadius: 3,
          p: { xs: 1.75, sm: 2.25 },
          textAlign: 'left',
          mb: 2.5,
          transition: 'border-color 0.3s ease',
          animation: done ? `${shake} 0.5s ease` : 'none',
        }}
      >
        {/* Header: icon + question */}
        <Stack direction="row" spacing={1.25} sx={{ mb: 1.25 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.5,
              flexShrink: 0,
              bgcolor: BRAND.gold,
              fontSize: '1.05rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            💰
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.25 }}>
            {t('onboarding.demo.market', {
              defaultValue: 'You will become a millionaire by the end of the year',
            })}
          </Typography>
        </Stack>

        {/* Meta: category · volume · chance */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Chip
              label={t('onboarding.demo.category', { defaultValue: 'Lifestyle' })}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 700,
                bgcolor: 'rgba(255,255,255,0.06)',
                color: BRAND.textSecondary,
              }}
            />
            <Typography sx={{ fontSize: '0.75rem', color: BRAND.textMuted }}>$2.4M Vol</Typography>
          </Stack>
          <Typography
            key={price}
            sx={{
              fontSize: '0.85rem',
              fontWeight: 800,
              color: BRAND.blueBright,
              animation: `${bump} 0.3s ease`,
            }}
          >
            {price}% {t('onboarding.demo.chance', { defaultValue: 'chance' })}
          </Typography>
        </Stack>

        {/* Bigger price chart */}
        <Sparkline points={history} color={done ? BRAND.green : BRAND.blueBright} />

        {/* Outcomes — real Porvenix Yes (green) / No (red) pills. Yes is selected. */}
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Box
            sx={{
              flex: 1,
              borderRadius: '12px',
              px: 1.5,
              py: 1.1,
              display: 'flex',
              justifyContent: 'space-between',
              bgcolor: BRAND.yesBg,
              color: BRAND.yesText,
              fontWeight: 700,
              border: `1px solid ${BRAND.yesText}`,
              boxShadow: `0 0 0 3px ${BRAND.yesBg}`,
            }}
          >
            <span>{t('onboarding.demo.yes', { defaultValue: 'Yes' })}</span>
            <span>{price}¢</span>
          </Box>
          <Box
            sx={{
              flex: 1,
              borderRadius: '12px',
              px: 1.5,
              py: 1.1,
              display: 'flex',
              justifyContent: 'space-between',
              bgcolor: BRAND.noBg,
              color: BRAND.noText,
              fontWeight: 700,
              border: `1px solid ${BRAND.noBorder}`,
              opacity: 0.85,
            }}
          >
            <span>{t('onboarding.demo.no', { defaultValue: 'No' })}</span>
            <span>{100 - price}¢</span>
          </Box>
        </Stack>

        {/* Fill progress (spam meter) */}
        <Box
          sx={{
            height: 6,
            borderRadius: 999,
            bgcolor: 'rgba(255,255,255,0.08)',
            overflow: 'hidden',
            mt: 1.75,
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${progress}%`,
              borderRadius: 999,
              background: done
                ? BRAND.green
                : `linear-gradient(90deg, ${BRAND.blue}, ${BRAND.blueBright})`,
              transition: 'width 0.25s ease',
            }}
          />
        </Box>
      </Box>

      {/* Buy button + floating pops */}
      <Box sx={{ position: 'relative' }}>
        {pops.map((p) => (
          <Box
            key={p.id}
            aria-hidden
            sx={{
              position: 'absolute',
              left: '50%',
              top: -8,
              fontSize: '1.4rem',
              pointerEvents: 'none',
              animation: `${floatUp} 0.85s ease-out forwards`,
            }}
          >
            {p.emoji}
          </Box>
        ))}

        <Button
          fullWidth
          onClick={handleBuy}
          disabled={done}
          disableElevation
          sx={{
            bgcolor: BRAND.green,
            color: '#ffffff',
            fontWeight: 800,
            fontSize: '1.02rem',
            textTransform: 'none',
            borderRadius: 2,
            py: 1.5,
            boxShadow: done ? `0 0 0 4px ${BRAND.green}33` : `0 8px 24px ${BRAND.green}44`,
            transition: 'transform 0.08s ease, background-color 0.2s ease, box-shadow 0.2s ease',
            '&.Mui-disabled': { bgcolor: BRAND.green, color: '#fff', opacity: 1 },
            '&:hover': { bgcolor: '#1f9d63' },
            '&:active': { transform: 'scale(0.96)' },
          }}
        >
          {done
            ? t('onboarding.demo.filled', { defaultValue: 'Order filled ✓' })
            : t('onboarding.demo.buyYes', { defaultValue: 'Buy Yes' })}
        </Button>
      </Box>

      <Typography
        sx={{
          color: done ? BRAND.green : BRAND.textMuted,
          fontSize: '0.8rem',
          mt: 1.25,
          fontWeight: done ? 700 : 400,
        }}
      >
        {done
          ? t('onboarding.demo.opened', { defaultValue: 'Position opened! Taking you in…' })
          : t('onboarding.demo.fillHint', { defaultValue: 'Tap Buy again to fill your order' })}
      </Typography>
    </Box>
  );
}

/** Inline SVG price chart that reacts to each tap. */
function Sparkline({ points, color }: { points: number[]; color: string }) {
  const { line, area, endX, endY } = useMemo(() => {
    const W = 100;
    const H = 40;
    const min = Math.min(...points);
    const max = Math.max(...points);
    const span = max - min || 1;
    const stepX = W / Math.max(1, points.length - 1);
    const xy = points.map((p, i) => {
      const x = i * stepX;
      const y = H - ((p - min) / span) * (H - 8) - 4;
      return [x, y] as const;
    });
    const line = xy.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
    const area = `${line} L${W},${H} L0,${H} Z`;
    const [endX, endY] = xy[xy.length - 1];
    return { line, area, endX, endY };
  }, [points]);

  return (
    <Box
      component="svg"
      viewBox="0 0 100 40"
      preserveAspectRatio="none"
      sx={{ width: '100%', height: { xs: 64, sm: 78 }, display: 'block' }}
      aria-hidden
    >
      <path d={area} fill={color} opacity={0.14} />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <circle cx={endX} cy={endY} r={2.8} fill={color} />
    </Box>
  );
}

export default TradeDemo;
